import { useSelector } from 'react-redux';
import { m } from 'framer-motion';
import { useState, useRef, useEffect } from 'react';
import { Box, Grid, Typography, Tooltip, IconButton, Link } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Container } from '@mui/system';
import { useTheme } from '@emotion/react';
import { PropTypes } from 'prop-types';
import { usePDF } from '@react-pdf/renderer';
import Iconify from '../../Iconify';
import useLocales from '../../../hooks/useLocales';
//  import { varFade } from '../../animate';
import MotionContainer from '../../animate/MotionContainer';
import ModalObjectFullscreen from '../../custom/Modals/ModalObjectFullscreen';
import DetailsTooltipsCO from '../../custom/Tooltip/DetailsTooltipsCO';
import { dispatch } from '../../../redux/store';
import { addToFavourites } from '../../../redux/slices/favourites';
import Licences from './Licence';
import DigitalObjects from './DigitalObjects';
import CultureObjectPrintDetail from '../../../views/CulturalObject/components/CultureObjectPrintDetail';
// import ImgBox from '../../custom/Box/ImgBox';
import Image from '../../Image';
import CustomMetaData from './CustomMetaData';
import { clearRelatedArticles, getRelatedArticles } from '../../../redux/slices/culturalObject';
import { GAEvent } from '../../GoogleAnalytics';

const atributtes = [
  'Author',
  'Date',
  'Title translation',
  'Subtitle',
  'Other title',
  'Part title',
  'Edition, nr',
  'Periodicity',
  'Source',
  'Co-authors',
  'Book designer',
  'Director',
  'Writer',
  'Screenwriter',
  'Director of photography',
  'Composer',
  'Dramaturge',
  'Costumes',
  'Editing',
  'Sound editor',
  'Post-production supervisor',
  'Actors',
  'Main director',
  'Performer',
  'Author of dialog',
  'Curator',
  'Other',
  'Publisher',
  'Ilustrator',
  'Photographer',
  'Translator',
  'Keeper',
  'Actor',
  'Issuer note',
  'Genre',
  'Style',
  'General use',
  'Series',
  'Producer',
  'ISBN',
  'ISSN',
  'Place of origin',
  'Date of finding',
  'Country',
  'County',
  'Municipality',
  'Street',
  'Number of census sheet',
  'Box number',
  'Language',
  'Source language',
  'Material',
  'Technique used',
  'Last',
  'Format',
  'Physical description',
  'Labelling',
  'Description',
  'Author of assignment',
  'Date of Declaration NKP',
  'Date of recording',
  'Keywords',
  'Originality',
  'Fund department',
  'Owner',
  'Asset number',
  'Identifier',
  'License',
  'The exercise of rights',
  'URL',
  "Object's rights",
  'Category',
  'Subcategory',
];

const RootStyle = styled('div')(({ theme }) => ({
  padding: theme.spacing(8, 0),
  backgroundColor: theme.palette.background.paper,
}));

// ----------------------------------------------------------------------

ObjectDetail.propTypes = {
  detail: PropTypes.any,
};

export default function ObjectDetail({ detail }) {
  const theme = useTheme();
  const { translate, currentLang } = useLocales();
  const [open, setOpen] = useState(false);
  const iconSize = 28;
  const { favourites } = useSelector((state) => state.favourites);
  const { relatedArticles } = useSelector((state) => state.culturalObject);
  const { digitalObjects } = useSelector((state) => state.culturalObject);
  const refProp = useRef();
  const [download, setDownload] = useState(false);

  const [printInstance, updatePrintInstance] = usePDF({
    document: <CultureObjectPrintDetail culturalObject={detail} digitalObjects={digitalObjects} />,
  });

  useEffect(() => {
    updatePrintInstance({
      document: <CultureObjectPrintDetail culturalObject={detail} digitalObjects={digitalObjects} />,
    });
    // ked tam dam aj "updatePrintInstance" tak to toci donekonecna
    // eslint-disable-next-line
  }, [detail, digitalObjects]);

  const isKO =
    window.location.pathname.includes('kulturne-objekty') ||
    window.location.pathname.includes('edu-materialy') ||
    window.location.pathname.includes('videoteka');
  const lang = currentLang.getValue === 'skSK' ? 'sk-SK' : 'en-GB';
  const capitalizeFirst = (str) => str?.charAt(0).toUpperCase() + str?.slice(1);

  const dateModifed = {
    name: 'Update date',
    values: [
      detail.dateModified
        ? new Date(detail.dateModified).toLocaleString(lang, { year: 'numeric', month: 'long', day: 'numeric' })
        : '-',
    ],
  };

  useEffect(() => {
    if (isKO) dispatch(getRelatedArticles({ key: detail.key, locale: currentLang.getValue }));
    return () => dispatch(clearRelatedArticles());
  }, [detail, currentLang, isKO]);

  const heart = (newItem) => {
    if (window.location.pathname.includes('scitacie-harky')) {
      const arr = { ...newItem };
      arr.municipality = arr.attributes
        .filter((item) => item.name === 'Municipality')
        .map((item) => item.values[0])?.[0];
      arr.censusSheetStreet = arr.attributes.filter((item) => item.name === 'Street').map((item) => item.values[0]);
      arr.category = arr.categories.filter((item) => item.categoryKey.length === 3)?.[0]?.name;

      dispatch(addToFavourites({ ...arr, type: 'sheet' }));
    } else {
      const arr = { ...newItem };

      dispatch(addToFavourites({ ...arr, type: 'object' }));
    }
  };

  const downloadClick = () => {
    if (window.location.pathname.includes('edu-materialy')) {
      GAEvent('button download', 'ButtonEDUDownload', 'Downloaded  object');
    }

    setDownload(true);
  };

  const hideDownload = () => {
    setDownload(false);
  };

  const array = detail.key ? detail.attributes : detail.metadata;
  const sortedArray = atributtes.reduce((acc, item) => {
    if (item === 'Category' && detail.categories?.length) {
      const parents = detail.categories.filter((e) => e.parentKey === '10' && e.parentKey !== '70').map((e) => e.name);
      const children = detail.categories
        .filter((e) => e.parentKey !== '10' && !detail.isCensusSheet)
        .map((e) => e.name);
      if (parents.length) {
        if (children.length) {
          acc.push({ name: item, values: parents }, { name: 'Subcategory', values: children });
        } else {
          acc.push({ name: item, values: parents });
        }
      }
    }
    const found = array.find((e) => e.name === item);
    if (found) {
      acc.push(found);
    }
    return acc;
  }, []);

  const metadata = (
    <>
      {[...sortedArray, dateModifed].map((item, index) => (
        <CustomMetaData
          key={index}
          item={item}
          theme={theme}
          currentLang={currentLang}
          translate={translate}
          categories={detail.categories}
          tags={detail.tags}
          itterable
          isCS={detail.isCensusSheet}
        />
      ))}
    </>
  );

  const pathArticles = window.location.pathname.includes('en/')
    ? `/en${translate('urls.articles')}`
    : translate('urls.articles');

  const mentionedIn = !!relatedArticles.length && (
    <Grid container spacing={1} sx={{ marginTop: 1 }}>
      {relatedArticles.map((item, idx) => (
        <Grid key={idx} xs={12} item>
          <Link onClick={() => window.open(`${pathArticles}/${item.key}`, '_blank')}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 'bold',
                textDecoration: 'underline !important',
                '&:hover': {
                  color: 'primary.main',
                  cursor: 'pointer',
                  textDecoration: 'underline !important',
                  backgroundColor: 'none',
                },
              }}
            >
              {item.name}
            </Typography>
          </Link>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <>
      <RootStyle ref={refProp}>
        <Container component={MotionContainer} sx={{ position: 'relative', height: '100%' }}>
          <m.div>
            <Typography variant="h14" className="print-title" sx={{ textAlign: 'center', marginBottom: '32px' }}>
              {capitalizeFirst(detail.title ?? detail.name)}
            </Typography>
            <Grid container spacing={2}>
              <Grid xs={12} item id="print-image" sx={{ visibility: 'hidden', height: 0 }}>
                <Image
                  src={
                    detail.thumbnail !== undefined && detail.thumbnail !== ''
                      ? `${process.env.REACT_APP_API_URL}/image/download/${detail.thumbnail}?sizeIndex=0`
                      : ''
                  }
                  sx={{ width: '100%', height: '450px' }}
                />
              </Grid>
              <Grid xs={12} item sx={{ visibility: 'hidden', display: 'none' }} id="print-metadata">
                {metadata}
              </Grid>

              <Grid xs={12} md={7} item>
                <Grid xs={12} item id="print-digital">
                  <DigitalObjects
                    objects={detail.digitalObjects}
                    allow={detail.objectUse}
                    digitalized={detail.isDigitalized}
                    // digitalized
                    download={download}
                    onCloseDownload={hideDownload}
                  />
                </Grid>
                <Grid id="print-icons" xs={12} item sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'inline-flex' }}>
                    {detail.isDigitalized && (
                      <Tooltip
                        title={<Licences license={detail?.license} />}
                        placement="right"
                        arrow
                        leaveDelay={500}
                        enterTouchDelay={0}
                        leaveTouchDelay={10000}
                      >
                        <IconButton
                          aria-label="tooltip"
                          sx={{
                            color: theme.palette.text.primary,
                            '&:focus': { backgroundColor: 'transparent' },
                            '&:hover': { backgroundColor: 'transparent' },
                            '&:visited': { backgroundColor: 'transparent' },
                            '&:link': { backgroundColor: 'transparent' },
                            '&:active': {
                              backgroundColor: 'transparent',
                            },
                          }}
                          disableRipple
                          disableFocusRipple
                          disableTouchRipple
                        >
                          <Iconify
                            icon={detail?.license ? 'ooui:logo-cc' : 'ic:baseline-copyright'}
                            width={iconSize}
                            height={iconSize}
                          />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>

                  <Box sx={{ display: 'inline-flex', fontSize: '24px' }}>
                    <DetailsTooltipsCO
                      licence={detail?.license}
                      favorite={favourites.findIndex((s) => s.key === detail.key) !== -1}
                      refProp={refProp}
                      title={detail?.title ?? detail?.name}
                      isFavorite={false}
                      heartAction={() => heart(detail)}
                      printInstance={printInstance}
                      size={iconSize}
                      onDownloadClick={detail?.downloadable ? downloadClick : undefined}
                      gaEventInfo={detail.key}
                    />
                  </Box>
                </Grid>
              </Grid>
              <Grid xs={12} md={5} item>
                <Grid
                  className="hiddenScroll noprintable"
                  id="noprintable"
                  sx={{ maxHeight: '480px', overflowY: 'auto', width: '100%' }}
                >
                  {metadata}
                </Grid>
                <Grid xs={12} item sx={{ marginTop: '22px' }}>
                  {!!relatedArticles.length && (
                    <Typography variant="h6" sx={{ color: 'link.yellowLink' }}>
                      {translate('default.mentionId')}
                    </Typography>
                  )}
                  {mentionedIn}
                </Grid>
              </Grid>
            </Grid>
          </m.div>
        </Container>
      </RootStyle>
      {detail.images && <ModalObjectFullscreen open={open} onClose={() => setOpen(false)} items={detail.images} />}
    </>
  );
}

// const getDate = (d) => {
// const date = new Date(d);
// let dt = date.getDate();
// let month = date.getMonth() + 1;
// const year = date.getFullYear();

// if (dt < 10) {
//   dt = `0${  dt}`;
// }
// if (month < 10) {
//   month = `0${  month}`;
// }

// return `${dt}.${month}.${year}`;
// }
